import { useMediaQuery } from "@material-ui/core";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Coordinate,
  TooltipProps,
  ResponsiveContainer,
  TooltipPayload,
  LegendProps,
} from "recharts";
import { SortId, YieldHistory } from "types/YieldHistory";
import { dateToString, dateToMonthAndYear } from "utility/date";
import { asPercent } from "utility/numbers";
import ChartLoader from "./ChartLoader";

const CustomTooltip = ({ active, payload }: TooltipProps) => {
  if (active) {
    return (
      <p
        className="intro"
        style={{
          color: "#444443",
          fontWeight: 300,
          background: "rgb(255, 255, 255, 0.5)",
          padding: 4,
        }}
      >
        {dateToString(
          (payload ?? [])[0]?.payload?.ValueDate as Date | undefined
        )}
      </p>
    );
  }

  return <></>;
};

const renderLegend = (
  props: LegendProps,
  legendValues: number[],
  matchesMediaQuery: boolean
) => {
  const { payload } = props;
  return (
    <ul
      style={{
        padding: 0,
        margin: 0,
        display: "flex",
        justifyContent: matchesMediaQuery ? "center" : "space-around",
        flexDirection: "row",
        flexWrap: "wrap",
        flexFlow: "row wrap",
        listStyle: "none",
      }}
    >
      {payload
        ?.map((entry, index) => {
          return (
            <li
              key={`item-${index}`}
              style={{
                height: 35,
                width: matchesMediaQuery
                  ? entry.value !== "Tuotto"
                    ? "60%"
                    : "20%"
                  : "fit-content",
                verticalAlign: "top",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <svg
                className="recharts-surface"
                viewBox="0 0 32 32"
                style={{
                  width: 14,
                  height: 14,
                  display: "inline-block",
                  verticalAlign: "middle",
                  marginRight: 4,
                  paddingTop: 3,
                }}
              >
                <line
                  strokeWidth={entry.value === "Tuotto" ? 4 : 2}
                  fill="none"
                  stroke={entry.color}
                  x1="0"
                  y1="16"
                  x2="32"
                  y2="16"
                  className="recharts-legend-icon"
                ></line>
              </svg>
              <span className="recharts-legend-item-text">{entry.value}</span>
              {legendValues.length > 0 && (
                <span style={{ color: entry?.color, marginLeft: 8 }}>
                  {(asPercent(legendValues[index ?? 0]) as string).replace(
                    "%",
                    ""
                  )}
                </span>
              )}
            </li>
          );
        })
        .reverse()}
    </ul>
  );
};

interface Props {
  data: YieldHistory[];
  index?: boolean;
  loading?: boolean;
}

const LineChartComponent = (props: Props) => {
  const { data, index, loading } = props;

  let legendValues = [] as number[];

  const { t } = useTranslation();

  const matchesMediaQuery = useMediaQuery("(min-width: 600px)");

  // tslint:disable-next-line: no-object-literal-type-assertion
  const tooltipPosition = { y: 0 } as Coordinate;

  const customerData = data.filter((d) => d.SortId === SortId.Customer);
  const indexData = data.filter((d) => d.SortId === SortId.Index);
  const indexName = indexData[0]?.Category;
  const mappedData = customerData.map((d, i) => ({
    ...d,
    IndexValue: indexData[i]?.Value,
  }));

  const yValues = [
    ...mappedData.map((d) => d.Value),
    ...mappedData.map((d) => d.IndexValue),
  ].filter(Boolean);

  const minY = Math.floor(Math.min(...yValues, 90) / 10) * 10;
  const maxY = Math.ceil(Math.max(...yValues, 110) / 10) * 10;

  return (
    <>
      <ResponsiveContainer height={300} width="100%">
        <LineChart
          data={mappedData}
          margin={{
            top: 5,
            right: matchesMediaQuery ? 30 : 0,
            left: matchesMediaQuery ? 20 : 0,
            bottom: 5,
          }}
          onMouseLeave={() => {
            legendValues = [];
          }}
          onMouseMove={(chartProps: { activePayload?: TooltipPayload[] }) => {
            const { activePayload } = chartProps;

            legendValues = activePayload?.map((x) => x.value as number) ?? [];
          }}
        >
          <CartesianGrid vertical={false} fill="white" color="#DDDDDD" />
          <XAxis
            dataKey="ValueDate"
            tickFormatter={dateToMonthAndYear}
            interval={
              matchesMediaQuery
                ? Math.round(mappedData.length / 8)
                : Math.round(mappedData.length / 5)
            }
          />
          <YAxis
            domain={[minY, maxY]}
            allowDataOverflow
            allowDecimals={false}
            scale="linear"
            width={matchesMediaQuery ? 60 : 35}
          />
          <Tooltip
            cursor={{ stroke: "#ACACAC", strokeWidth: 1, strokeDasharray: "2" }}
            active={false}
            content={CustomTooltip}
            position={tooltipPosition}
          />
          <Legend
            iconType="plainline"
            content={(legendProps: LegendProps) =>
              renderLegend(legendProps, legendValues, matchesMediaQuery)
            }
          />
          {index && indexData.length > 0 && (
            <Line
              name={indexName}
              type="monotone"
              dot={false}
              dataKey="IndexValue"
              stroke="#D3741C"
              activeDot={{ r: 5 }}
            />
          )}
          <Line
            name={t<string>("profit")}
            type="monotone"
            dot={false}
            dataKey="Value"
            stroke="#003366"
            activeDot={{ r: 5 }}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
      {loading && (
        <div
          style={{
            height: 225,
            width: "100%",
            position: "absolute",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            top: 50,
            right: 0,
          }}
        >
          <ChartLoader />
        </div>
      )}
    </>
  );
};

export default React.memo(LineChartComponent);
